import { useCallback } from 'react';

import { useFeatureFlags, useScanEditingContextSlice, useSyncStatus } from '@kaiizen/kai-pro-api-react-state';

import { ANALYTICS_EVENTS, trackEvent } from '../analytics';

export const useCancelChangeOrderAction = () => {
  const { featureFlags } = useFeatureFlags();
  const { scan, scanMutators, scanIsEditable, effectiveScanType } = useScanEditingContextSlice([
    'scan',
    'scanMutators',
    'scanIsEditable',
    'effectiveScanType',
  ]);

  // Mirrors ScopeToolbar.tsx's showCancelButton / ScanReview.tsx's isChangeRequest
  // && !isApproved: available for the whole life of a change order, not just
  // while it's still a draft, so it stays visible through submitted-for-review too.
  // Reads effectiveScanType (a live client-side diff against baseline, same as
  // useScopeSubmissionAction's canSubmit), not the raw scan.scanType field -
  // that field only gets recomputed server-side after a scan-data write round-trips
  // back, so it would lag a beat behind Submit Change Order appearing the
  // instant a task is edited.
  // Excludes 'rejected' alongside 'approved': both are terminal, non-editable
  // outcomes (server-side validateScanIsEditable refuses to edit either), but
  // getScanType deliberately keeps a rejected scan's historical scanType at
  // 'changeRequest' rather than rewriting it away - so this action-gating
  // check, not the type derivation, is what hides the button for a rejected
  // scan.
  const canEditCurrentProjectWork = !featureFlags.turnBasedHandoff || scanIsEditable;
  const canCancel = canEditCurrentProjectWork
    && effectiveScanType === 'changeRequest'
    && scan?.scanLifecycleStatus !== 'approved'
    && scan?.scanLifecycleStatus !== 'rejected';

  // Task edits are debounced then pushed through an offline-first sync queue
  // (useSaveScanData.ts) rather than saved synchronously, so the server's own
  // scan.scanType (which cancelChangeOrder validates against) can still be
  // stale for a beat after a local edit makes canCancel true. isReadyToCancel
  // tells the UI when that queue has actually drained for this scan, so the
  // confirm button can stay disabled until the cancel is guaranteed to succeed
  // server-side, rather than silently failing on a premature click.
  const { scanDataSyncStatus } = useSyncStatus(scan?.id);
  const isReadyToCancel = scanDataSyncStatus.pending === 0;

  const cancelChangeOrder = useCallback(async () => {
    if (!scan || !canEditCurrentProjectWork) return;
    // No navigation afterward: cancelChangeOrder reverts this same scan id in
    // place (back to its last-approved baseline, demoted to scanType
    // 'progress') rather than deleting it or creating a new one, and its
    // onSuccess callback already refreshes the scan-query cache and refetches
    // the reverted data delta - so staying on this scan's page just re-renders
    // it with the reverted content.
    await scanMutators.cancelChangeOrder(scan.id);
    trackEvent(ANALYTICS_EVENTS.scanChangeOrderCanceled, { scanId: scan.id });
  }, [canEditCurrentProjectWork, scan, scanMutators]);

  return { canCancel, isReadyToCancel, cancelChangeOrder };
};
